import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Paper, Typography, Divider, Chip, Stack } from "@mui/material";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import DiamondRoundedIcon from "@mui/icons-material/DiamondRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { decodeTicketData } from "./ticketQrCodec";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

// A small labelled row used throughout the card.
const InfoRow = ({ label, value }) =>
  value ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ color: "#64748B", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "#1E293B", textAlign: "right" }}
      >
        {value}
      </Typography>
    </Box>
  ) : null;

const SectionTitle = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 2 }}>
    {icon}
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "#2563EB",
        fontSize: "0.72rem",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const PawnTicketView = () => {
  const location = useLocation();

  const { data, error } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const d = params.get("d");
    if (!d) return { data: null, error: "No ticket data found in this link." };
    try {
      return { data: decodeTicketData(d), error: null };
    } catch (e) {
      return { data: null, error: "This ticket link is invalid or corrupted." };
    }
  }, [location.search]);

  const isReceipt = data?.ticket?.type === "receipt";

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
        }}
      >
        <Paper
          sx={{ p: 4, borderRadius: 3, textAlign: "center", maxWidth: 360 }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Ticket unavailable
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const { store, ticket, customer, loan, ornaments } = data;
  const headlineLabel = isReceipt ? "Total Paid" : "Total Loan";
  const headlineValue = isReceipt ? loan.paidAmount : loan.principal;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 2, sm: 5 },
        px: 2,
        background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 440,
          borderRadius: 4,
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        {/* Header / hero */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)",
            color: "#fff",
            px: 3,
            pt: 3,
            pb: 3.5,
            textAlign: "center",
            position: "relative",
          }}
        >
          {store.image ? (
            <Box
              component="img"
              src={store.image}
              alt={store.name}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                objectFit: "cover",
                border: "3px solid rgba(255,255,255,0.85)",
                mb: 1.5,
                bgcolor: "#fff",
              }}
            />
          ) : (
            <StorefrontRoundedIcon sx={{ fontSize: 48, mb: 1 }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {store.name || "Pawn Shop"}
          </Typography>

          <Stack
            spacing={0.25}
            sx={{ mt: 1, alignItems: "center", opacity: 0.95 }}
          >
            {store.address && (
              <Typography
                variant="caption"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <LocationOnRoundedIcon sx={{ fontSize: 15 }} />
                {store.address}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              {store.phone && (
                <Typography
                  variant="caption"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <PhoneRoundedIcon sx={{ fontSize: 15 }} />
                  {store.phone}
                </Typography>
              )}
              {store.email && (
                <Typography
                  variant="caption"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <EmailRoundedIcon sx={{ fontSize: 15 }} />
                  {store.email}
                </Typography>
              )}
            </Stack>
            {store.pblId && (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                P.B.L No.: {store.pblId}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Ticket type + voucher badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1.5,
            bgcolor: "#faf5ff",
            borderBottom: "1px dashed #e2d4f5",
          }}
        >
          <Chip
            size="small"
            label={isReceipt ? "Payment Receipt" : "Pawn Ticket"}
            sx={{
              fontWeight: 700,
              bgcolor: "#2563EB",
              color: "#fff",
            }}
          />
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" sx={{ color: "#64748B" }}>
              Voucher No.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {ticket.voucherNo || "—"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          {/* Headline amount */}
          <Box
            sx={{
              mt: 2.5,
              mb: 1,
              p: 2,
              borderRadius: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
              border: "1px solid #e2d4f5",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#2563EB", fontWeight: 700 }}
            >
              {headlineLabel.toUpperCase()}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#4c1d95" }}>
              {rupees(headlineValue)}
            </Typography>
            {(ticket.date || ticket.time) && (
              <Typography variant="caption" sx={{ color: "#64748B" }}>
                {formatDate(ticket.date)}
                {ticket.time ? ` • ${ticket.time}` : ""}
              </Typography>
            )}
          </Box>

          {/* Customer */}
          <SectionTitle
            icon={<PersonRoundedIcon sx={{ fontSize: 18, color: "#2563EB" }} />}
          >
            Customer
          </SectionTitle>
          <InfoRow label="Name" value={customer.name} />
          <InfoRow label="Mobile" value={customer.mobile} />
          <InfoRow label="Address" value={customer.address} />

          {/* Loan */}
          <Divider sx={{ mt: 1.5 }} />
          <SectionTitle
            icon={
              <VerifiedRoundedIcon sx={{ fontSize: 18, color: "#2563EB" }} />
            }
          >
            {isReceipt ? "Payment Details" : "Loan Details"}
          </SectionTitle>
          {isReceipt ? (
            <>
              <InfoRow label="Amount Paid" value={rupees(loan.paidAmount)} />
              {loan.interestAmount > 0 && (
                <InfoRow label="Interest" value={rupees(loan.interestAmount)} />
              )}
            </>
          ) : (
            <>
              {loan.itemValue > 0 && (
                <InfoRow label="Item Value" value={rupees(loan.itemValue)} />
              )}
              <InfoRow
                label="Principal Amount"
                value={rupees(loan.principal)}
              />
              {/* {loan.interestRate > 0 && (
                <InfoRow
                  label="Interest Rate"
                  value={`${loan.interestRate}% per month`}
                />
              )}
              {loan.interestAmount > 0 && (
                <InfoRow
                  label="Interest Amount"
                  value={rupees(loan.interestAmount)}
                />
              )} */}
            </>
          )}
          {/* {loan.scheme && <InfoRow label="Scheme" value={loan.scheme} />} */}

          {/* Ornaments */}
          {ornaments && ornaments.length > 0 && (
            <>
              <Divider sx={{ mt: 1.5 }} />
              <SectionTitle
                icon={
                  <DiamondRoundedIcon sx={{ fontSize: 18, color: "#2563EB" }} />
                }
              >
                Ornaments ({ornaments.length})
              </SectionTitle>
              <Stack spacing={1}>
                {ornaments.map((o, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                      border: "1px solid #eef2f7",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {o.name || `Item ${idx + 1}`}
                        {o.pcs ? ` · ${o.pcs} pcs` : ""}
                      </Typography>
                      {o.grossWeight > 0 && (
                        <Typography variant="caption" sx={{ color: "#64748B" }}>
                          Gross Wt: {o.grossWeight.toFixed(3)} g
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#4c1d95" }}
                    >
                      {rupees(o.value)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}

          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: "#94A3B8",
              mt: 3,
            }}
          >
            This is a read-only copy generated from a scanned ticket.
          </Typography>

          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              display: "block",
              textAlign: "center",
              color: "#d0d0d0",
              mt: 5,
            }}
          >
            {/* © Kumudu Software Solutions<sup>™</sup> */}© Kumudu Software
            Solutions Pvt. Ltd.<sup>™</sup>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default PawnTicketView;
